import React, { useEffect, useState } from 'react';
import { Autocomplete, Popper, TextField } from '@mui/material';
import * as R from 'ramda';

import { Icon } from '@atom/mui';
import { GoogleMapsLocation } from '@atom/types/gmapsLocation';

const styles = {
  leftIconMarkerStyle: {
    minWidth: '20px',
    maxWidth: '20px',
    paddingRight: '5px',
  },
  menuItem: {
    fontSize: 13,
    overflow: 'hidden',
    paddingRight: '1rem',
    paddingLeft: '1rem',
  },
};

interface Props {
  initialValue?: string;
  disabled?: boolean;
  hintText?: string;
  textFieldStyle?: object;
  onSelect?: (place: GoogleMapsLocation, searchTerm?: string) => any;
  anchor?: any;
  popoverWidth?: string | number;
}

const isLatitude = num => isFinite(num) && Math.abs(num) <= 90;
const isLongitude = num => isFinite(num) && Math.abs(num) <= 180;

const GooglePlacesAutoComplete = ({
  initialValue,
  hintText,
  textFieldStyle,
  onSelect,
  anchor,
  popoverWidth,
}: Props) => {
  const [searchText, setSearchText] = useState<string>(initialValue);
  const [data, setData] = useState([]);

  useEffect(() => {
    setSearchText(initialValue);
  }, [initialValue]);

  const google = window.google;
  const geocoder = new google.maps.Geocoder();
  const service = new google.maps.places.AutocompleteService();

  const getLatLgn = (locationID, cb) => {
    geocoder.geocode({ placeId: locationID }, (onSelectUpdate, status) => {
      cb(onSelectUpdate, status);
    });
  };

  const updateInput = searchTextUpdate => {
    if (!R.isEmpty(searchTextUpdate)) {
      service.getPlacePredictions({ input: searchTextUpdate }, predictions => {
        if (predictions) {
          setData(predictions);
        }
      });
    }
    setSearchText(searchTextUpdate);
  };

  const onNewRequest = (_, newValue) => {
    const [latitude, longitude] = searchText.split(',');

    if (
      isLatitude(parseFloat(latitude)) &&
      isLongitude(parseFloat(longitude))
    ) {
      onSelect({
        lat: parseFloat(latitude),
        lng: parseFloat(longitude),
      });
    }

    getLatLgn(newValue.place_id, results => {
      onSelect(
        {
          lat: results[0].geometry.location.lat(),
          lng: results[0].geometry.location.lng(),
        },
        results[0].formatted_address,
      );

      setSearchText(results[0].formatted_address);
    });
  };

  const popoverProps = {
    style: {
      ...(popoverWidth && {
        width: popoverWidth,
      }),
    },
    ...(anchor && {
      anchorEl: anchor,
    }),
  };

  const getMenuItems = (props, option) => {
    const { key, ...optionProps } = props;

    return (
      <li key={key} style={styles.menuItem} {...optionProps}>
        <Icon style={styles.leftIconMarkerStyle}>location_on</Icon>
        <span>{option?.description}</span>
      </li>
    );
  };

  // The boilerplate required to get this to work with google maps comes
  // directly from the mui documentation on the Autocomplete component
  return (
    <Autocomplete
      options={data}
      freeSolo
      clearIcon={null}
      filterOptions={option => option}
      getOptionLabel={option =>
        typeof option === 'string' ? option : option.description
      }
      renderOption={getMenuItems}
      inputValue={searchText || ''}
      onInputChange={(event, newInputValue) => updateInput(newInputValue)}
      onChange={onNewRequest}
      PopperComponent={params => <Popper {...params} {...popoverProps} />}
      renderInput={params => (
        <TextField
          variant="standard"
          {...params}
          placeholder={hintText || ''}
          fullWidth
          style={{ paddingTop: '0.25rem', ...textFieldStyle }}
          InputProps={{ disableUnderline: true, ...params.InputProps }}
        />
      )}
      style={{ width: '100%' }}
    />
  );
};

export default GooglePlacesAutoComplete;
